import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import {
  Lock,
  Clock,
  Hash,
  Globe,
  Shield,
  Eye,
  EyeOff,
  Calendar,
  MapPin,
  Bot,
  AlertTriangle,
  Check,
} from 'lucide-react';
import { format } from 'date-fns';

interface QRSecuritySettingsProps {
  qrCode: {
    id: string;
    name: string;
    passwordEnabled?: boolean;
    expiresAt?: string | null;
    scanLimit?: number | null;
    uniqueScanLimit?: number | null;
    geoWhitelist?: string[] | null;
    geoBlacklist?: string[] | null;
    blockedRedirectUrl?: string | null;
    captchaEnabled?: boolean;
    botProtectionEnabled?: boolean;
  } | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
}

const COUNTRIES = [
  'United States', 'United Kingdom', 'Canada', 'Australia', 'Germany',
  'France', 'Spain', 'Italy', 'Netherlands', 'Japan', 'China', 'India',
  'Brazil', 'Mexico', 'Singapore', 'South Korea', 'Russia', 'South Africa',
];

export function QRSecuritySettings({
  qrCode,
  open,
  onOpenChange,
  onSave,
}: QRSecuritySettingsProps) {
  const [activeTab, setActiveTab] = useState('password');
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Form state
  const [passwordEnabled, setPasswordEnabled] = useState(false);
  const [password, setPassword] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [scanLimit, setScanLimit] = useState('');
  const [uniqueScanLimit, setUniqueScanLimit] = useState('');
  const [geoMode, setGeoMode] = useState<'none' | 'whitelist' | 'blacklist'>('none');
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [blockedRedirectUrl, setBlockedRedirectUrl] = useState('');
  const [captchaEnabled, setCaptchaEnabled] = useState(false);
  const [botProtectionEnabled, setBotProtectionEnabled] = useState(false);

  // Load existing settings when QR code changes
  useState(() => {
    if (qrCode) {
      setPasswordEnabled(qrCode.passwordEnabled || false);
      setExpiresAt(qrCode.expiresAt ? format(new Date(qrCode.expiresAt), "yyyy-MM-dd'T'HH:mm") : '');
      setScanLimit(qrCode.scanLimit?.toString() || '');
      setUniqueScanLimit(qrCode.uniqueScanLimit?.toString() || '');
      setBlockedRedirectUrl(qrCode.blockedRedirectUrl || '');
      setCaptchaEnabled(qrCode.captchaEnabled || false);
      setBotProtectionEnabled(qrCode.botProtectionEnabled || false);
      
      if (qrCode.geoWhitelist && qrCode.geoWhitelist.length > 0) {
        setGeoMode('whitelist');
        setSelectedCountries(qrCode.geoWhitelist);
      } else if (qrCode.geoBlacklist && qrCode.geoBlacklist.length > 0) {
        setGeoMode('blacklist');
        setSelectedCountries(qrCode.geoBlacklist);
      } else {
        setGeoMode('none');
        setSelectedCountries([]);
      }
    }
  });

  const handleSave = async () => {
    if (!qrCode) return;

    setSaving(true);
    try {
      // Update security settings
      const updates: Record<string, unknown> = {
        password_enabled: passwordEnabled,
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
        scan_limit: scanLimit ? parseInt(scanLimit) : null,
        unique_scan_limit: uniqueScanLimit ? parseInt(uniqueScanLimit) : null,
        geo_whitelist: geoMode === 'whitelist' ? selectedCountries : null,
        geo_blacklist: geoMode === 'blacklist' ? selectedCountries : null,
        blocked_redirect_url: blockedRedirectUrl || null,
        captcha_enabled: captchaEnabled,
        bot_protection_enabled: botProtectionEnabled,
      };

      const { error } = await supabase
        .from('qr_codes')
        .update(updates)
        .eq('id', qrCode.id);

      if (error) throw error;

      // If password is set, use the secure function
      if (passwordEnabled && password) {
        const { error: pwError } = await supabase.rpc('set_qr_password', {
          p_qr_code_id: qrCode.id,
          p_password: password,
        });

        if (pwError) throw pwError;
      }

      toast.success('Security settings saved');
      onSave();
      onOpenChange(false);
    } catch (error: any) {
      toast.error(error.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleCountry = (country: string) => {
    setSelectedCountries((prev) =>
      prev.includes(country)
        ? prev.filter((c) => c !== country)
        : [...prev, country]
    );
  };

  if (!qrCode) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Security Settings
          </DialogTitle>
          <DialogDescription>
            Configure security features for "{qrCode.name}"
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="password" className="flex items-center gap-1.5 text-xs">
              <Lock className="h-3.5 w-3.5" />
              Password
            </TabsTrigger>
            <TabsTrigger value="expiration" className="flex items-center gap-1.5 text-xs">
              <Clock className="h-3.5 w-3.5" />
              Expiration
            </TabsTrigger>
            <TabsTrigger value="limits" className="flex items-center gap-1.5 text-xs">
              <Hash className="h-3.5 w-3.5" />
              Limits
            </TabsTrigger>
            <TabsTrigger value="geo" className="flex items-center gap-1.5 text-xs">
              <Globe className="h-3.5 w-3.5" />
              Geo
            </TabsTrigger>
            <TabsTrigger value="fraud" className="flex items-center gap-1.5 text-xs">
              <Bot className="h-3.5 w-3.5" />
              Protection
            </TabsTrigger>
          </TabsList>

          <div className="mt-6 min-h-[300px]">
            <TabsContent value="password" className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/50">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-primary/10">
                    <Lock className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-medium">Password Protection</h4>
                    <p className="text-sm text-muted-foreground">
                      Require password to access this QR code
                    </p>
                  </div>
                </div>
                <Switch
                  checked={passwordEnabled}
                  onCheckedChange={setPasswordEnabled}
                />
              </div>

              <AnimatePresence>
                {passwordEnabled && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="space-y-4"
                  >
                    <div className="space-y-2">
                      <Label>Set Password</Label>
                      <div className="relative">
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Enter password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Users will need this password to access the QR destination
                      </p>
                    </div>

                    <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                      <div className="flex gap-2">
                        <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                        <div className="text-sm">
                          <p className="font-medium text-amber-700 dark:text-amber-400">
                            Security Note
                          </p>
                          <p className="text-muted-foreground">
                            After 5 failed attempts, access will be locked for 30 minutes
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </TabsContent>

            <TabsContent value="expiration" className="space-y-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Expiration Date & Time
                </Label>
                <Input
                  type="datetime-local"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  QR code will stop working after this date
                </p>
              </div>

              {expiresAt && (
                <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                  <p className="text-sm">
                    This QR will expire on{' '}
                    <strong>{format(new Date(expiresAt), 'PPP p')}</strong>
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label>Custom Expired Message Redirect</Label>
                <Input
                  type="url"
                  placeholder="https://example.com/expired"
                  value={blockedRedirectUrl}
                  onChange={(e) => setBlockedRedirectUrl(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Redirect to this URL when expired (optional)
                </p>
              </div>
            </TabsContent>

            <TabsContent value="limits" className="space-y-4">
              <div className="space-y-2">
                <Label>Total Scan Limit</Label>
                <Input
                  type="number"
                  placeholder="e.g., 100"
                  value={scanLimit}
                  onChange={(e) => setScanLimit(e.target.value)}
                  min="1"
                />
                <p className="text-xs text-muted-foreground">
                  Maximum number of total scans allowed
                </p>
              </div>

              <div className="space-y-2">
                <Label>Per-Device Limit</Label>
                <Input
                  type="number"
                  placeholder="e.g., 1"
                  value={uniqueScanLimit}
                  onChange={(e) => setUniqueScanLimit(e.target.value)}
                  min="1"
                />
                <p className="text-xs text-muted-foreground">
                  Maximum scans per unique IP address
                </p>
              </div>

              {(scanLimit || uniqueScanLimit) && (
                <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                  <div className="flex gap-2">
                    <Check className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-green-700 dark:text-green-400">
                        Limits Active
                      </p>
                      <p className="text-muted-foreground">
                        {scanLimit && `Total: ${scanLimit} scans`}
                        {scanLimit && uniqueScanLimit && ' • '}
                        {uniqueScanLimit && `Per device: ${uniqueScanLimit} scans`}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="geo" className="space-y-4">
              <div className="space-y-2">
                <Label>Geographic Restrictions</Label>
                <div className="grid grid-cols-3 gap-2">
                  <Button
                    type="button"
                    variant={geoMode === 'none' ? 'default' : 'outline'}
                    onClick={() => {
                      setGeoMode('none');
                      setSelectedCountries([]);
                    }}
                    className="w-full"
                  >
                    No Restrictions
                  </Button>
                  <Button
                    type="button"
                    variant={geoMode === 'whitelist' ? 'default' : 'outline'}
                    onClick={() => setGeoMode('whitelist')}
                    className="w-full"
                  >
                    Whitelist
                  </Button>
                  <Button
                    type="button"
                    variant={geoMode === 'blacklist' ? 'default' : 'outline'}
                    onClick={() => setGeoMode('blacklist')}
                    className="w-full"
                  >
                    Blacklist
                  </Button>
                </div>
              </div>

              <AnimatePresence>
                {geoMode !== 'none' && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="space-y-3"
                  >
                    <Label>
                      {geoMode === 'whitelist'
                        ? 'Allow only these countries'
                        : 'Block these countries'}
                    </Label>
                    <div className="flex flex-wrap gap-2 p-3 rounded-lg border max-h-40 overflow-y-auto">
                      {COUNTRIES.map((country) => (
                        <Badge
                          key={country}
                          variant={
                            selectedCountries.includes(country)
                              ? 'default'
                              : 'outline'
                          }
                          className="cursor-pointer"
                          onClick={() => toggleCountry(country)}
                        >
                          {country}
                        </Badge>
                      ))}
                    </div>
                    {selectedCountries.length > 0 && (
                      <p className="text-sm text-muted-foreground">
                        <MapPin className="h-3.5 w-3.5 inline mr-1" />
                        {selectedCountries.length} countries selected
                      </p>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </TabsContent>

            <TabsContent value="fraud" className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/50">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-primary/10">
                    <Bot className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-medium">Bot Protection</h4>
                    <p className="text-sm text-muted-foreground">
                      Block automated scanners and crawlers
                    </p>
                  </div>
                </div>
                <Switch
                  checked={botProtectionEnabled}
                  onCheckedChange={setBotProtectionEnabled}
                />
              </div>

              <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/50">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-primary/10">
                    <Shield className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-medium">CAPTCHA Verification</h4>
                    <p className="text-sm text-muted-foreground">
                      Show CAPTCHA for suspicious traffic
                    </p>
                  </div>
                </div>
                <Switch
                  checked={captchaEnabled}
                  onCheckedChange={setCaptchaEnabled}
                />
              </div>

              {(botProtectionEnabled || captchaEnabled) && (
                <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
                  <div className="flex gap-2">
                    <Shield className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-primary dark:text-primary">
                        Fraud Protection Active
                      </p>
                      <p className="text-muted-foreground">
                        Suspicious traffic will be challenged or blocked
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>
          </div>
        </Tabs>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
